import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, Save, Trash2, Copy, CheckCircle, XCircle } from 'lucide-react';

const VehicleMasterManagement = ({ API_BASE_URL, ocrRecognizedName, currentData }) => {
  const [loading, setLoading] = useState(false);
  const [gridRef, setGridRef] = useState(null);
  const containerRef = useRef(null);
  const retryCountRef = useRef(0); // OCR 인식 차량명 검색 재시도 카운터
  
  // 폼 데이터 (상단 폼 - 추가/수정용)
  const [formData, setFormData] = useState({
    BASE_CAR_NO: '', // ID (수정 시에만 사용, 추가 시에는 빈 값)
    BASE_CAR_NM: '', // 차량명(한글)
    BASE_CAR_NM_ENG: '', // 차량명(영문)
    BASE_CAR_MAKER: '', // 브랜드
    BASE_CAR_YEAR: '', // 연식
    BASE_CAR_G_PUM: '', // 거래품명
    BASE_CAR_PUM: '', // 품명
    BASE_CAR_CC: '', // 배기량
    BASE_CAR_WEIGHT: '', // 중량
    BASE_CAR_CBM: '' // CBM
  });
  
  const [isAddMode, setIsAddMode] = useState(false); // 추가 모드 여부
  const [isFormEnabled, setIsFormEnabled] = useState(false); // 폼 활성화 여부 (기본 비활성)
  
  const [selectedVehicle, setSelectedVehicle] = useState(null); // 선택된 행
  const [formKey, setFormKey] = useState(0); // 폼 강제 리렌더링을 위한 key
  const [toast, setToast] = useState(null); // 토스트 알림 상태

  // AUIGrid 컬럼 정의 (인라인 필터 포함)
  const columnLayout = useMemo(() => [
    {
      dataField: "BASE_CAR_NO",
      headerText: "ID",
      width: 60,
      editable: false,
      style: "center-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      }
    },
    {
      dataField: "BASE_CAR_NM",
      headerText: "차량명(한글)",
      width: 160,
      editable: false,
      style: "left-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      }
    },
    {
      dataField: "BASE_CAR_NM_ENG",
      headerText: "차량명(영문)",
      width: 160,
      editable: false,
      style: "left-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      },
      labelFunction: function(rowIndex, columnIndex, value, headerText, item) {
        // 영문명이 없으면 "추가" 버튼 표시
        if (!value || value.trim() === '') {
          return '<span style="color: #3b82f6; text-decoration: underline; cursor: pointer;">추가</span>';
        }
        return value || '-';
      }
    },
    {
      dataField: "BASE_CAR_MAKER",
      headerText: "브랜드",
      width: 100,
      editable: false,
      style: "left-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      }
    },
    {
      dataField: "BASE_CAR_YEAR",
      headerText: "연식",
      width: 70,
      editable: false,
      style: "center-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      }
    },
    {
      dataField: "BASE_CAR_CC",
      headerText: "배기량",
      width: 90,
      editable: false,
      style: "right-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      },
      labelFunction: function(rowIndex, columnIndex, value) {
        if (!value) return '-';
        return Number(value).toLocaleString();
      }
    },
    {
      dataField: "BASE_CAR_WEIGHT",
      headerText: "중량",
      width: 90,
      editable: false,
      style: "right-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      },
      labelFunction: function(rowIndex, columnIndex, value) {
        if (!value) return '-';
        return Number(value).toLocaleString();
      }
    },
    {
      dataField: "BASE_CAR_CBM",
      headerText: "CBM",
      width: 70,
      editable: false,
      style: "right-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      },
      labelFunction: function(rowIndex, columnIndex, value) {
        if (!value) return '-';
        return Number(value).toLocaleString();
      }
    },
    {
      dataField: "REG_DT",
      headerText: "등록일시",
      width: 160, // 시간이 잘리지 않도록 충분한 너비 확보
      editable: false,
      style: "center-align",
      filter: {
        inline: true // 인라인 필터 활성화 (공식 데모와 동일)
      },
      labelFunction: function(rowIndex, columnIndex, value, headerText, item, dataField) {
        if (!value) return '-';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return value; // 유효하지 않은 날짜면 원본 반환
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hour = String(date.getHours()).padStart(2, '0');
          const minute = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day} ${hour}:${minute}`;
        } catch (e) {
          return value; // 에러 발생 시 원본 반환
        }
      }
    }
  ], []);

  // AUIGrid 속성 정의 (인라인 필터 활성화)
  const gridProps = useMemo(() => ({
    headerHeight: 32,
    rowHeight: 32,
    fillColumnSizeMode: "lastColumnFill", // 마지막 컬럼이 남은 공간을 채우도록 설정
    enableSorting: true,
    enableFilter: true, // 필터 활성화
    showInlineFilter: true, // 인라인 필터 행 표시
    filterApplyDelay: 300, // 입력 후 300ms 후 필터링 (공식 데모와 동일)
    showRowCheckColumn: false,
    showRowNumColumn: true,
    rowNumHeaderText: "No",
    editable: false,
    selectionMode: "singleRow",
    selectionStyle: {
      backgroundColor: "#E5E7EB", // 연한 노란색 (선택된 행)
      color: "#1A1B1E" // 로고 색상 텍스트
    }
  }), []);

  // 차량 목록 조회
  const fetchVehicleList = useCallback(async (currentGridRef = null) => {
    setLoading(true);
    try {
      const groupCode = localStorage.getItem('groupCode') || '1000';
      
      const response = await fetch(`${API_BASE_URL}/api/vehicle-master/list?groupCode=${groupCode}&limit=1000`);
      
      if (!response.ok) {
        console.error('❌ API 응답 오류:', response.status, response.statusText);
        return [];
      }
      
      const result = await response.json();
      
      if (result.success) {
        const data = result.data || [];
        
        const gridToUse = currentGridRef || gridRef;
        if (gridToUse && window.AUIGrid) {
          window.AUIGrid.setGridData(gridToUse, data);
        }
        
        return data; // 데이터 반환
      }
      return [];
    } catch (error) {
      console.error('❌ 차량 목록 조회 실패:', error);
      alert('차량 목록 조회 중 오류가 발생했습니다: ' + error.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, gridRef]);

  // 행 선택 핸들러 (useCallback으로 안정화)
  const handleRowSelect = useCallback((item) => {
    console.log("🔵 handleRowSelect 호출됨:", item);
    
    if (!item) {
      console.warn("🔵 item이 없습니다.");
      return;
    }
    
    // 상태 업데이트를 즉시 실행
    setSelectedVehicle(item);
    setIsAddMode(false); // 수정 모드로 전환
    
    const newFormData = {
      BASE_CAR_NO: item.BASE_CAR_NO || '',
      BASE_CAR_NM: item.BASE_CAR_NM || '',
      BASE_CAR_NM_ENG: item.BASE_CAR_NM_ENG || '',
      BASE_CAR_MAKER: item.BASE_CAR_MAKER || '',
      BASE_CAR_YEAR: item.BASE_CAR_YEAR || '',
      BASE_CAR_G_PUM: item.BASE_CAR_G_PUM || '',
      BASE_CAR_PUM: item.BASE_CAR_PUM || '',
      BASE_CAR_CC: item.BASE_CAR_CC || '',
      BASE_CAR_WEIGHT: item.BASE_CAR_WEIGHT || '',
      BASE_CAR_CBM: item.BASE_CAR_CBM || ''
    };
    
    console.log("🔵 폼 데이터 설정:", newFormData);
    
    // 상태 업데이트를 동시에 실행
    setFormData(newFormData);
    setIsFormEnabled(true); // 폼 활성화
    setFormKey(prev => prev + 1); // 폼 강제 리렌더링
    
    console.log("🔵 상태 업데이트 완료 - isFormEnabled: true, formData:", newFormData);
  }, []);

  // AUIGrid 초기화
  useEffect(() => {
    if (!window.AUIGrid || !containerRef.current) return;

    const gridId = 'vehicle_master_grid';
    let grid = null;
    let resizeHandler = null;

    if (gridRef) {
      try {
        window.AUIGrid.destroy(gridRef);
      } catch (e) {
        console.warn('그리드 제거 실패:', e);
      }
    }

    const timer = setTimeout(() => {
      const gridElement = document.getElementById(gridId);
      if (!gridElement) {
        console.error(`그리드 DOM 요소를 찾을 수 없습니다: ${gridId}`);
        return;
      }

      grid = window.AUIGrid.create(`#${gridId}`, columnLayout, gridProps);
      setGridRef(grid);

      // 행 선택 이벤트 (rowClick)
      window.AUIGrid.bind(grid, "rowClick", function(event) {
        console.log("🔵 rowClick 이벤트 발생:", event);
        if (event && event.item) {
          handleRowSelect(event.item);
        }
      });
      
      // 행 선택 변경 이벤트 (selectionChange) - 더 안정적
      window.AUIGrid.bind(grid, "selectionChange", function(event) {
        console.log("🔵 selectionChange 이벤트 발생:", event);
        if (event && event.selectedItems && event.selectedItems.length > 0) {
          const selectedItem = event.selectedItems[0].item;
          handleRowSelect(selectedItem);
        }
      });

      // 셀 클릭 이벤트 (행 선택 처리)
      window.AUIGrid.bind(grid, "cellClick", function(event) {
        console.log("🔵 cellClick 이벤트 발생:", event);
        const { item, dataField } = event;
        
        // 영문명 추가 버튼 클릭 처리 (특수 케이스)
        if (dataField === 'BASE_CAR_NM_ENG' && (!item.BASE_CAR_NM_ENG || item.BASE_CAR_NM_ENG.trim() === '')) {
          // 영문명 추가 - 상단 폼에 로드 (수정 모드)
          setSelectedVehicle(item);
          setIsAddMode(false);
          setIsFormEnabled(true); // 폼 활성화
          setFormData({
            BASE_CAR_NO: item.BASE_CAR_NO || '',
            BASE_CAR_NM: item.BASE_CAR_NM || '',
            BASE_CAR_NM_ENG: '',
            BASE_CAR_MAKER: item.BASE_CAR_MAKER || '',
            BASE_CAR_YEAR: item.BASE_CAR_YEAR || '',
            BASE_CAR_G_PUM: item.BASE_CAR_G_PUM || '',
            BASE_CAR_PUM: item.BASE_CAR_PUM || '',
            BASE_CAR_CC: item.BASE_CAR_CC || '',
            BASE_CAR_WEIGHT: item.BASE_CAR_WEIGHT || '',
            BASE_CAR_CBM: item.BASE_CAR_CBM || ''
          });
        } else {
          // 일반 셀 클릭 시 행 선택 처리
          if (item) {
            console.log("🔵 cellClick에서 handleRowSelect 호출:", item);
            handleRowSelect(item);
          }
        }
      });

      // 필터 변경 이벤트 바인딩 (디버깅용)
      window.AUIGrid.bind(grid, "filterChange", function(event) {
        console.log("🔵 filterChange 이벤트 발생:", event);
      });

      // 필터 적용 이벤트 바인딩 (디버깅용)
      window.AUIGrid.bind(grid, "filterApply", function(event) {
        console.log("🔵 filterApply 이벤트 발생:", event);
      });

      resizeHandler = () => {
        if (grid) {
          window.AUIGrid.resize(grid);
        }
      };
      window.addEventListener("resize", resizeHandler);

      setTimeout(() => {
        fetchVehicleList(grid).then(() => {
          // 그리드 데이터 로드 완료 후 OCR 인식 차량명 자동 검색
          if (ocrRecognizedName) {
            setTimeout(() => {
              const gridData = window.AUIGrid.getGridData(grid);
              if (gridData && gridData.length > 0) {
                const foundVehicle = gridData.find(item => 
                  item.BASE_CAR_NM && item.BASE_CAR_NM.trim() === ocrRecognizedName.trim()
                );
                
                if (foundVehicle) {
                  const rowIndex = gridData.findIndex(item => item.BASE_CAR_NO === foundVehicle.BASE_CAR_NO);
                  if (rowIndex >= 0) {
                    console.log('🔵 차량명 매칭 발견 (그리드 로드 후):', foundVehicle, '행 인덱스:', rowIndex);
                    // AUIGrid에서 행 선택: setSelectionByIndex(rowIndex, columnIndex) 사용
                    window.AUIGrid.setSelectionByIndex(grid, rowIndex, 0);
                    setTimeout(() => {
                      handleRowSelect(foundVehicle);
                    }, 200);
                  }
                } else {
                  // 찾지 못한 경우: 신규등록 모드로 전환
                  console.log('🔵 차량명 매칭 없음 (그리드 로드 후), 신규등록 모드로 전환');
                  setIsAddMode(true);
                  setIsFormEnabled(true);
                  
                  const newFormData = {
                    BASE_CAR_NO: '',
                    BASE_CAR_NM: ocrRecognizedName.trim(),
                    BASE_CAR_NM_ENG: '',
                    BASE_CAR_MAKER: '',
                    BASE_CAR_YEAR: '',
                    BASE_CAR_G_PUM: '',
                    BASE_CAR_PUM: '',
                    BASE_CAR_CC: currentData?.displacement || currentData?.cc || '',
                    BASE_CAR_WEIGHT: currentData?.weight || currentData?.totalWeight || '',
                    BASE_CAR_CBM: currentData?.cbm || ''
                  };
                  
                  setFormData(newFormData);
                  setFormKey(prev => prev + 1);
                }
              }
            }, 300);
          }
          
          // 데이터 로드 후 필터 활성화 확인
          setTimeout(() => {
            if (grid && window.AUIGrid && window.AUIGrid.applyFilter) {
              // 필터가 제대로 작동하는지 확인
              console.log('🔵 그리드 필터 상태 확인:', window.AUIGrid.getFilterInfo ? window.AUIGrid.getFilterInfo(grid) : 'getFilterInfo not available');
            }
          }, 500);
        });
      }, 300);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (resizeHandler) {
        window.removeEventListener("resize", resizeHandler);
      }
      if (grid) {
        try {
          window.AUIGrid.destroy(grid);
        } catch (e) {
          console.warn('그리드 cleanup 실패:', e);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnLayout, gridProps, API_BASE_URL, fetchVehicleList, handleRowSelect, ocrRecognizedName, currentData]);

  // OCR 인식 차량명 자동 검색 및 선택 (그리드가 이미 로드된 후)
  useEffect(() => {
    if (!ocrRecognizedName || !gridRef || !window.AUIGrid) return;
    
    retryCountRef.current = 0; // 재시도 카운터 초기화
    const maxRetries = 5;
    
    const searchAndSelectVehicle = () => {
      try {
        // 그리드에서 직접 데이터 가져오기
        const gridData = window.AUIGrid.getGridData(gridRef);
        if (!gridData || gridData.length === 0) {
          console.log(`🔵 그리드 데이터가 아직 로드되지 않았습니다. 재시도 ${retryCountRef.current + 1}/${maxRetries}`);
          retryCountRef.current++;
          if (retryCountRef.current < maxRetries) {
            setTimeout(searchAndSelectVehicle, 500);
          }
          return;
        }
        
        console.log('🔵 그리드 데이터 로드 완료, 검색 시작:', ocrRecognizedName, '데이터 개수:', gridData.length);
        
        // 차량명(한글)이 일치하는 항목 찾기
        const foundVehicle = gridData.find(item => 
          item.BASE_CAR_NM && item.BASE_CAR_NM.trim() === ocrRecognizedName.trim()
        );
        
        if (foundVehicle) {
          // 찾은 경우: 해당 행 선택 및 폼에 데이터 채우기
          const rowIndex = gridData.findIndex(item => item.BASE_CAR_NO === foundVehicle.BASE_CAR_NO);
          if (rowIndex >= 0) {
            console.log('🔵 차량명 매칭 발견:', foundVehicle, '행 인덱스:', rowIndex);
            // 행 선택: AUIGrid의 올바른 API 사용
            window.AUIGrid.setSelectionByIndex(gridRef, rowIndex, 0);
            // 약간의 지연 후 폼에 데이터 채우기 (그리드 선택이 완료된 후)
            setTimeout(() => {
              handleRowSelect(foundVehicle);
            }, 200);
          }
        } else {
          // 찾지 못한 경우: 신규등록 모드로 전환하고 폼에 데이터 채우기
          console.log('🔵 차량명 매칭 없음, 신규등록 모드로 전환');
          setIsAddMode(true);
          setIsFormEnabled(true);
          
          const newFormData = {
            BASE_CAR_NO: '',
            BASE_CAR_NM: ocrRecognizedName.trim(),
            BASE_CAR_NM_ENG: '',
            BASE_CAR_MAKER: '',
            BASE_CAR_YEAR: '',
            BASE_CAR_G_PUM: '',
            BASE_CAR_PUM: '',
            BASE_CAR_CC: currentData?.displacement || currentData?.cc || '',
            BASE_CAR_WEIGHT: currentData?.weight || currentData?.totalWeight || '',
            BASE_CAR_CBM: currentData?.cbm || ''
          };
          
          setFormData(newFormData);
          setFormKey(prev => prev + 1);
        }
      } catch (error) {
        console.error('❌ OCR 인식 차량명 자동 검색 실패:', error);
      }
    };
    
    // 그리드가 로드된 후 약간의 지연을 두고 검색 (그리드 데이터가 로드될 때까지 대기)
    const timer = setTimeout(searchAndSelectVehicle, 1000);
    return () => clearTimeout(timer);
  }, [ocrRecognizedName, gridRef, currentData, handleRowSelect]);

  // 갱신 이벤트 리스너 추가
  useEffect(() => {
    const handleRefresh = () => {
      if (gridRef) {
        fetchVehicleList(gridRef);
      }
    };
    
    window.addEventListener('refreshVehicleMaster', handleRefresh);
    
    return () => {
      window.removeEventListener('refreshVehicleMaster', handleRefresh);
    };
  }, [gridRef, fetchVehicleList]);

  // 새 차량 추가 모드로 전환
  const handleNewVehicle = () => {
    setSelectedVehicle(null);
    setIsAddMode(true);
    setIsFormEnabled(true); // 폼 활성화
    setFormData({
      BASE_CAR_NO: '',
      BASE_CAR_NM: '',
      BASE_CAR_NM_ENG: '',
      BASE_CAR_MAKER: '',
      BASE_CAR_YEAR: '',
      BASE_CAR_G_PUM: '',
      BASE_CAR_PUM: '',
      BASE_CAR_CC: '',
      BASE_CAR_WEIGHT: '',
      BASE_CAR_CBM: ''
    });
    // 그리드 선택 해제
    if (gridRef && window.AUIGrid) {
      window.AUIGrid.clearSelection(gridRef);
    }
  };

  // 폼 데이터 변경 핸들러
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 저장 핸들러 (추가 또는 수정)
  const handleSave = async () => {
    // 필수 필드 검증
    if (!formData.BASE_CAR_NM || !formData.BASE_CAR_NM.trim()) {
      alert('차량명(한글)은 필수 입력 항목입니다.');
      return;
    }
    if (!formData.BASE_CAR_NM_ENG || !formData.BASE_CAR_NM_ENG.trim()) {
      alert('차량명(영문)은 필수 입력 항목입니다.');
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || 'system';
      const groupCode = localStorage.getItem('groupCode') || '1000';
      
      if (isAddMode || !formData.BASE_CAR_NO) {
        // 추가 모드
        const response = await fetch(`${API_BASE_URL}/api/vehicle-master/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            carNameKr: formData.BASE_CAR_NM,
            carNameEn: formData.BASE_CAR_NM_ENG,
            brand: formData.BASE_CAR_MAKER,
            modelYear: formData.BASE_CAR_YEAR,
            tradeItemName: formData.BASE_CAR_G_PUM || '',
            itemName: formData.BASE_CAR_PUM || '',
            displacement: formData.BASE_CAR_CC,
            weight: formData.BASE_CAR_WEIGHT,
            cbm: formData.BASE_CAR_CBM,
            groupCode: groupCode,
            userId: userId
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // 토스트 알림 표시
            setToast({ type: 'success', message: '차량이 추가되었습니다.' });
            setTimeout(() => setToast(null), 3000); // 3초 후 자동 사라짐
            await fetchVehicleList(gridRef);
            
            // 저장된 차량 데이터를 이벤트로 전달 (모달 갱신용)
            const savedVehicleData = {
              BASE_CAR_NO: result.data?.BASE_CAR_NO || result.data?.master_id,
              BASE_CAR_NM: formData.BASE_CAR_NM,
              BASE_CAR_NM_ENG: formData.BASE_CAR_NM_ENG,
              BASE_CAR_MAKER: formData.BASE_CAR_MAKER,
              BASE_CAR_YEAR: formData.BASE_CAR_YEAR,
              BASE_CAR_G_PUM: formData.BASE_CAR_G_PUM,
              BASE_CAR_PUM: formData.BASE_CAR_PUM,
              BASE_CAR_CC: formData.BASE_CAR_CC,
              BASE_CAR_WEIGHT: formData.BASE_CAR_WEIGHT,
              BASE_CAR_CBM: formData.BASE_CAR_CBM
            };
            window.dispatchEvent(new CustomEvent('vehicleMasterSaved', { detail: savedVehicleData }));
            
            // 폼 초기화
            setFormData({
              BASE_CAR_NO: '',
              BASE_CAR_NM: '',
              BASE_CAR_NM_ENG: '',
              BASE_CAR_MAKER: '',
              BASE_CAR_YEAR: '',
              BASE_CAR_G_PUM: '',
              BASE_CAR_PUM: '',
              BASE_CAR_CC: '',
              BASE_CAR_WEIGHT: '',
              BASE_CAR_CBM: ''
            });
            setSelectedVehicle(null);
            setIsAddMode(false);
            setIsFormEnabled(false);
            setFormKey(prev => prev + 1);
          } else {
            alert('차량 추가 실패: ' + (result.message || '알 수 없는 오류'));
          }
        } else {
          alert('차량 추가 실패');
        }
      } else {
        // 수정 모드
        const savedId = formData.BASE_CAR_NO;
        
        const response = await fetch(`${API_BASE_URL}/api/vehicle-master/${formData.BASE_CAR_NO}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            BASE_CAR_NM: formData.BASE_CAR_NM,
            BASE_CAR_NM_ENG: formData.BASE_CAR_NM_ENG,
            BASE_CAR_MAKER: formData.BASE_CAR_MAKER,
            BASE_CAR_YEAR: formData.BASE_CAR_YEAR,
            BASE_CAR_G_PUM: formData.BASE_CAR_G_PUM || '',
            BASE_CAR_PUM: formData.BASE_CAR_PUM || '',
            BASE_CAR_CC: formData.BASE_CAR_CC,
            BASE_CAR_WEIGHT: formData.BASE_CAR_WEIGHT,
            BASE_CAR_CBM: formData.BASE_CAR_CBM,
            userId: userId
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // 토스트 알림 표시
            setToast({ type: 'success', message: '차량 정보가 저장되었습니다.' });
            setTimeout(() => setToast(null), 3000); // 3초 후 자동 사라짐
            await fetchVehicleList(gridRef);
            
            // 저장된 차량 데이터를 이벤트로 전달 (모달 갱신용)
            const savedVehicleData = {
              BASE_CAR_NO: formData.BASE_CAR_NO,
              BASE_CAR_NM: formData.BASE_CAR_NM,
              BASE_CAR_NM_ENG: formData.BASE_CAR_NM_ENG,
              BASE_CAR_MAKER: formData.BASE_CAR_MAKER,
              BASE_CAR_YEAR: formData.BASE_CAR_YEAR,
              BASE_CAR_G_PUM: formData.BASE_CAR_G_PUM,
              BASE_CAR_PUM: formData.BASE_CAR_PUM,
              BASE_CAR_CC: formData.BASE_CAR_CC,
              BASE_CAR_WEIGHT: formData.BASE_CAR_WEIGHT,
              BASE_CAR_CBM: formData.BASE_CAR_CBM
            };
            window.dispatchEvent(new CustomEvent('vehicleMasterSaved', { detail: savedVehicleData }));
            
            // 저장된 행 다시 선택
            setTimeout(() => {
              if (gridRef && window.AUIGrid) {
                const gridData = window.AUIGrid.getGridData(gridRef);
                const updatedItem = gridData.find(item => item.BASE_CAR_NO === savedId);
                if (updatedItem) {
                  const rowIndex = gridData.indexOf(updatedItem);
                  window.AUIGrid.setSelectionByIndex(gridRef, rowIndex);
                  setSelectedVehicle(updatedItem);
                  setFormData({
                    BASE_CAR_NO: updatedItem.BASE_CAR_NO || '',
                    BASE_CAR_NM: updatedItem.BASE_CAR_NM || '',
                    BASE_CAR_NM_ENG: updatedItem.BASE_CAR_NM_ENG || '',
                    BASE_CAR_MAKER: updatedItem.BASE_CAR_MAKER || '',
                    BASE_CAR_YEAR: updatedItem.BASE_CAR_YEAR || '',
                    BASE_CAR_G_PUM: updatedItem.BASE_CAR_G_PUM || '',
                    BASE_CAR_PUM: updatedItem.BASE_CAR_PUM || '',
                    BASE_CAR_CC: updatedItem.BASE_CAR_CC || '',
                    BASE_CAR_WEIGHT: updatedItem.BASE_CAR_WEIGHT || '',
                    BASE_CAR_CBM: updatedItem.BASE_CAR_CBM || ''
                  });
                  setIsAddMode(false);
                }
              }
            }, 100);
            
          } else {
            alert('차량 정보 저장에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
          }
        } else {
          alert('차량 정보 저장에 실패했습니다.');
        }
      }
    } catch (error) {
      console.error('차량 정보 저장 실패:', error);
      alert('차량 정보 저장 중 오류가 발생했습니다.');
    }
  };

  // 삭제 핸들러
  const handleDelete = async () => {
    if (isAddMode || !formData.BASE_CAR_NO) {
      alert('삭제할 차량을 선택해주세요.\n\n하단 그리드에서 삭제할 행을 클릭한 후 삭제 버튼을 클릭하세요.');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`정말로 "${formData.BASE_CAR_NM || formData.BASE_CAR_NM_ENG}" 차량을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicle-master/${formData.BASE_CAR_NO}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // 토스트 알림 표시
          setToast({ type: 'success', message: '차량이 삭제되었습니다.' });
          setTimeout(() => setToast(null), 3000); // 3초 후 자동 사라짐
          // 폼 초기화
          setFormData({
            BASE_CAR_NO: '',
            BASE_CAR_NM: '',
            BASE_CAR_NM_ENG: '',
            BASE_CAR_MAKER: '',
            BASE_CAR_YEAR: '',
            BASE_CAR_G_PUM: '',
            BASE_CAR_PUM: '',
            BASE_CAR_CC: '',
            BASE_CAR_WEIGHT: '',
            BASE_CAR_CBM: ''
          });
          setSelectedVehicle(null);
          setIsAddMode(false);
          setIsFormEnabled(false);
          setFormKey(prev => prev + 1);
          await fetchVehicleList(gridRef);
        } else {
          alert('차량 삭제에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
        }
      } else {
        alert('차량 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('차량 삭제 실패:', error);
      alert('차량 삭제 중 오류가 발생했습니다.');
    }
  };


  // 복사 (현재 선택된 차량을 기반으로 새 차량 추가)
  const handleCopy = () => {
    if (!selectedVehicle) {
      alert('복사할 차량을 선택해주세요.');
      return;
    }
    
    setSelectedVehicle(null);
    setIsAddMode(true); // 추가 모드로 전환
    setIsFormEnabled(true); // 폼 활성화
    setFormData({
      BASE_CAR_NO: '', // ID는 비워서 새로 생성되도록
      BASE_CAR_NM: formData.BASE_CAR_NM || '',
      BASE_CAR_NM_ENG: formData.BASE_CAR_NM_ENG || '',
      BASE_CAR_MAKER: formData.BASE_CAR_MAKER || '',
      BASE_CAR_YEAR: formData.BASE_CAR_YEAR || '',
      BASE_CAR_G_PUM: formData.BASE_CAR_G_PUM || '',
      BASE_CAR_PUM: formData.BASE_CAR_PUM || '',
      BASE_CAR_CC: formData.BASE_CAR_CC || '',
      BASE_CAR_WEIGHT: formData.BASE_CAR_WEIGHT || '',
      BASE_CAR_CBM: formData.BASE_CAR_CBM || ''
    });
    // 그리드 선택 해제
    if (gridRef && window.AUIGrid) {
      window.AUIGrid.clearSelection(gridRef);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white relative">
      {/* 토스트 알림 */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[9999] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            toast.type === 'success' 
              ? 'bg-gray-700 text-white' 
              : 'bg-gray-800 text-white'
          }`}
          style={{
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}
      
      {/* 상단 폼 영역 (테이블 형태로 공간 최소화) */}
      <div className="flex-shrink-0 px-2 py-2 border-b border-gray-200 bg-white" key={formKey}>
        <div className="flex gap-4">
            {/* 왼쪽: 테이블 형태 입력 필드들 */}
            <div className="flex-1">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 w-24 align-middle">
                      차량명(한글) *
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_NM}
                        onChange={(e) => handleFormChange('BASE_CAR_NM', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 쏘나타"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 w-24 align-middle">
                      차량명(영문) *
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_NM_ENG}
                        onChange={(e) => handleFormChange('BASE_CAR_NM_ENG', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: SONATA"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      브랜드
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_MAKER}
                        onChange={(e) => handleFormChange('BASE_CAR_MAKER', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 현대"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      연식
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_YEAR}
                        onChange={(e) => handleFormChange('BASE_CAR_YEAR', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 2011"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      배기량
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_CC}
                        onChange={(e) => handleFormChange('BASE_CAR_CC', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 1998"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      거래품명
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_G_PUM}
                        onChange={(e) => handleFormChange('BASE_CAR_G_PUM', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 거래품명"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      품명
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_PUM}
                        onChange={(e) => handleFormChange('BASE_CAR_PUM', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 품명"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      중량
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        value={formData.BASE_CAR_WEIGHT}
                        onChange={(e) => handleFormChange('BASE_CAR_WEIGHT', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 1740"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-700 align-middle">
                      CBM
                    </td>
                    <td className="px-2 py-1.5 align-middle" colSpan="3">
                      <input
                        type="text"
                        value={formData.BASE_CAR_CBM}
                        onChange={(e) => handleFormChange('BASE_CAR_CBM', e.target.value)}
                        disabled={!isFormEnabled}
                        className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold ${
                          !isFormEnabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="예: 13"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* 오른쪽: 버튼들 */}
            <div className="flex flex-col justify-start gap-1.5 w-24 flex-shrink-0">
              <button
                onClick={handleNewVehicle}
                className={`w-full px-3 py-1.5 rounded transition-colors text-xs flex items-center justify-center gap-1 border ${
                  isAddMode && isFormEnabled
                    ? 'bg-gray-200 text-gray-900 border-gray-400'
                    : 'bg-transparent text-gray-900 hover:bg-gray-100 border-gray-300'
                }`}
              >
                <Plus className="w-3 h-3 text-gray-900" />
                <span>신규등록</span>
              </button>
              <button
                onClick={handleSave}
                disabled={!isFormEnabled || !formData.BASE_CAR_NM || (!isAddMode && (!formData.BASE_CAR_NM_ENG || !selectedVehicle))}
                className={`w-full px-3 py-1.5 rounded transition-colors text-xs flex items-center justify-center gap-1 border ${
                  isAddMode && isFormEnabled && formData.BASE_CAR_NM
                    ? 'text-white border-[#1A1B1E] hover:opacity-90'
                    : 'bg-transparent text-gray-900 border-gray-300 hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200'
                }`}
                style={isAddMode && isFormEnabled && formData.BASE_CAR_NM ? { backgroundColor: '#1A1B1E' } : {}}
                title={isAddMode ? '새 차량 추가' : '차량 정보 수정'}
              >
                <Save className="w-3 h-3 text-current" />
                <span>저장</span>
              </button>
              <button
                onClick={handleCopy}
                disabled={!isFormEnabled || !selectedVehicle}
                className="w-full px-3 py-1.5 bg-transparent rounded transition-colors text-xs flex items-center justify-center gap-1 border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200 text-gray-900 hover:bg-gray-100"
                title="선택한 차량 복사하여 새로 추가"
              >
                <Copy className="w-3 h-3 text-current" />
                <span>복사</span>
              </button>
              <button
                onClick={handleDelete}
                disabled={!isFormEnabled || isAddMode || !selectedVehicle}
                className="w-full px-3 py-1.5 bg-transparent rounded transition-colors text-xs flex items-center justify-center gap-1 border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200 text-gray-900 hover:bg-gray-100"
                title="차량 삭제"
              >
                <Trash2 className="w-3 h-3 text-current" />
                <span>삭제</span>
              </button>
            </div>
          </div>
      </div>

      {/* 하단 그리드 영역 (인라인 필터 포함) */}
      <div className="flex-1 overflow-hidden" ref={containerRef} style={{ margin: 0, padding: 0 }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">로딩 중...</div>
          </div>
        ) : (
          <div
            id="vehicle_master_grid"
            style={{
              width: '100%',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
              margin: 0,
              padding: 0
            }}
          />
        )}
      </div>

    </div>
  );
};

export default VehicleMasterManagement;
